import { useEffect, useState, useRef } from "react";
import { ArrowLeft, Send, Paperclip, Bug, Plus, AlertCircle, CheckCircle2, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useNavigate } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Progress } from "@/components/ui/progress";
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "@/components/ui/table";

// Sample conversation data for bugs channel
const conversationData = [
  {
    id: 1,
    sender: "BugHunter",
    avatarSrc: "/crew-agents/intent-checker.png",
    message: "I've identified a critical issue with the dashboard performance when loading large datasets. System memory utilization spikes to 95% and response times exceed 10 seconds when processing more than 50,000 entries.",
    timestamp: "Today at 9:15 AM",
    isUser: false,
    issueSeverity: "high"
  },
  {
    id: 2,
    sender: "DevOpsAgent",
    avatarSrc: "/crew-agents/web-analytics-hello.mp4",
    message: "I've reproduced the issue and traced it to the data processing function in the real-time visualization component. It's not properly batch-processing large inputs.",
    timestamp: "Today at 9:22 AM",
    isUser: false
  },
  {
    id: 3,
    sender: "You",
    avatarSrc: "/crew-agents/page-semantic.png",
    message: "Can you identify which specific component is causing the bottleneck?",
    timestamp: "Today at 9:30 AM",
    isUser: true
  },
  {
    id: 4,
    sender: "DevOpsAgent",
    avatarSrc: "/crew-agents/web-analytics-hello.mp4",
    message: "Yes, it's in the src/components/visualization/RealTimeChart.js file. The useEffect hook that handles data updates is processing the entire dataset on every update instead of only the new data.",
    timestamp: "Today at 9:35 AM",
    isUser: false
  },
  {
    id: 5,
    sender: "BugHunter",
    avatarSrc: "/crew-agents/intent-checker.png",
    message: "I've created bug ticket #4872 for this issue. It's been assigned to the frontend team with high priority.",
    timestamp: "Today at 9:40 AM",
    isUser: false
  },
  {
    id: 6,
    sender: "AnalyticsBot",
    avatarSrc: "/crew-agents/webanalytics.png",
    message: "This is affecting approximately 15% of our enterprise customers right now. I recommend we implement a temporary solution while the proper fix is being developed.",
    timestamp: "Today at 9:45 AM",
    isUser: false
  },
  {
    id: 7,
    sender: "DevOpsAgent",
    avatarSrc: "/crew-agents/web-analytics-hello.mp4",
    message: "I can implement a temporary solution that limits the dataset size for real-time visualization to 10,000 entries with pagination for viewing the full dataset. This should resolve the immediate performance issues while we work on a more complete solution.",
    timestamp: "Today at 9:50 AM",
    isUser: false
  },
  {
    id: 8,
    sender: "You",
    avatarSrc: "/crew-agents/page-semantic.png",
    message: "That sounds good. Please implement the temporary fix and keep me updated on the progress of the permanent solution.",
    timestamp: "Today at 9:55 AM",
    isUser: true
  },
  {
    id: 9,
    sender: "DevOpsAgent",
    avatarSrc: "/crew-agents/web-analytics-hello.mp4",
    message: "I've implemented the temporary fix and deployed it to production. It's performing well in initial tests. The frontend team has estimated that they'll have the permanent fix ready by tomorrow afternoon.",
    timestamp: "Today at 10:30 AM",
    isUser: false
  },
  {
    id: 10,
    sender: "BugHunter",
    avatarSrc: "/crew-agents/intent-checker.png",
    message: "I've also discovered a minor UI issue in the mobile view of the analytics dashboard. Some chart labels are being truncated on smaller screens. I've created bug ticket #4873 for this with low priority.",
    timestamp: "Today at 11:15 AM",
    isUser: false,
    issueSeverity: "low"
  }
];

// Team members for this channel
const teamMembers = [
  {
    name: "BugHunter",
    role: "QA Specialist",
    avatarSrc: "/crew-agents/intent-checker.png",
    status: "online"
  },
  {
    name: "DevOpsAgent",
    role: "DevOps Engineer",
    avatarSrc: "/crew-agents/web-analytics-hello.mp4", 
    status: "online"
  },
  {
    name: "AnalyticsBot",
    role: "Analytics Specialist",
    avatarSrc: "/crew-agents/webanalytics.png",
    status: "away"
  },
  {
    name: "You",
    role: "Product Manager",
    avatarSrc: "/crew-agents/page-semantic.png",
    status: "online"
  }
];

// Active bugs
const activeBugs = [
  {
    id: "4872",
    title: "Dashboard performance issue with large datasets",
    component: "Visualization",
    severity: "high",
    status: "in-progress",
    assignee: "DevOpsAgent",
    created: "Today at 9:40 AM",
    updated: "Today at 10:30 AM",
    progress: 60
  },
  {
    id: "4873",
    title: "Mobile chart labels truncation",
    component: "UI/Mobile",
    severity: "low",
    status: "open",
    assignee: "Unassigned",
    created: "Today at 11:15 AM",
    updated: "Today at 11:15 AM",
    progress: 0
  },
  {
    id: "4865",
    title: "Export to PDF option fails with certain charts",
    component: "Export",
    severity: "medium",
    status: "in-progress",
    assignee: "BugHunter",
    created: "Yesterday at 2:20 PM",
    updated: "Today at 8:05 AM",
    progress: 75
  },
  {
    id: "4861",
    title: "Session timeout not respecting user settings",
    component: "Authentication",
    severity: "medium",
    status: "in-progress",
    assignee: "DevOpsAgent",
    created: "2 days ago",
    updated: "Yesterday at 4:30 PM",
    progress: 90
  }
];

const ChatMessage = ({ message }: { message: any }) => {
  const isUser = message.isUser;
  
  return (
    <div className={`flex gap-3 mb-4 ${isUser ? 'justify-end' : ''}`}>
      {!isUser && (
        <div className="flex-shrink-0">
          <div className="relative">
            <img 
              src={message.avatarSrc} 
              alt={message.sender}
              className="h-8 w-8 rounded-full object-cover"
            />
            {message.issueSeverity && (
              <div className={`absolute -top-1 -right-1 h-3 w-3 rounded-full border-2 border-background ${
                message.issueSeverity === 'high' ? 'bg-red-500' : 
                message.issueSeverity === 'medium' ? 'bg-yellow-500' : 'bg-blue-500'
              }`} />
            )}
          </div>
        </div>
      )}
      
      <div className={`max-w-[70%] ${isUser ? 'bg-primary text-primary-foreground' : 'bg-muted'} rounded-lg px-4 py-2`}>
        <div className="flex justify-between items-baseline mb-1">
          <span className={`font-medium text-sm ${isUser ? 'text-primary-foreground' : 'text-foreground'}`}>
            {message.sender}
          </span>
          <span className={`text-xs ml-4 ${isUser ? 'text-primary-foreground/70' : 'text-muted-foreground'}`}>
            {message.timestamp}
          </span>
        </div>
        <p className={`text-sm whitespace-pre-line ${isUser ? 'text-primary-foreground' : 'text-foreground'}`}>
          {message.message}
        </p>
        
        {message.issueSeverity && !isUser && (
          <div className="flex items-center gap-1 mt-2">
            <Bug className={`h-3 w-3 ${
              message.issueSeverity === 'high' ? 'text-red-500' : 
              message.issueSeverity === 'medium' ? 'text-yellow-500' : 'text-blue-500'
            }`} />
            <span className="text-xs text-muted-foreground capitalize">{message.issueSeverity} severity issue</span>
          </div>
        )}
      </div>
      
      {isUser && (
        <img 
          src={message.avatarSrc} 
          alt={message.sender}
          className="h-8 w-8 rounded-full object-cover mt-1"
        />
      )}
    </div>
  );
};

const BugRow = ({ bug }: { bug: any }) => {
  return (
    <TableRow>
      <TableCell className="py-2">
        <div className="flex items-center gap-2">
          <div className={`w-2 h-2 rounded-full ${
            bug.severity === 'high' ? 'bg-red-500' : 
            bug.severity === 'medium' ? 'bg-yellow-500' : 'bg-blue-500'
          }`} />
          <div>
            <div className="font-medium text-sm">#{bug.id} {bug.title}</div>
            <div className="text-xs text-muted-foreground">{bug.component}</div>
          </div>
        </div>
      </TableCell>
      <TableCell className="py-2">
        <Badge variant="outline" className={`
          ${bug.status === 'open' ? 'bg-blue-100 text-blue-800' : 
            bug.status === 'in-progress' ? 'bg-yellow-100 text-yellow-800' : 
            'bg-green-100 text-green-800'}
        `}>
          {bug.status}
        </Badge>
      </TableCell>
      <TableCell className="py-2">
        <div className="flex items-center gap-2">
          {bug.assignee !== 'Unassigned' ? (
            <>
              <Avatar className="h-6 w-6">
                <AvatarImage 
                  src={bug.assignee === 'BugHunter' ? '/crew-agents/intent-checker.png' : 
                        bug.assignee === 'DevOpsAgent' ? '/crew-agents/web-analytics-hello.mp4' : 
                        '/crew-agents/webanalytics.png'} 
                />
                <AvatarFallback>{bug.assignee[0]}</AvatarFallback>
              </Avatar>
              <span className="text-xs">{bug.assignee}</span>
            </>
          ) : (
            <span className="text-xs text-muted-foreground">Unassigned</span>
          )}
        </div>
      </TableCell>
      <TableCell className="py-2">
        <div className="w-full">
          <div className="flex justify-between text-xs mb-1">
            <span>{bug.progress}%</span>
            <span>{bug.updated}</span>
          </div>
          <Progress value={bug.progress} className="h-1.5" />
        </div>
      </TableCell>
      <TableCell className="py-2">
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="icon" className="h-7 w-7">
            <CheckCircle2 className="h-4 w-4 text-muted-foreground" />
          </Button>
          <Button variant="ghost" size="icon" className="h-7 w-7">
            <Trash2 className="h-4 w-4 text-muted-foreground" />
          </Button>
        </div>
      </TableCell>
    </TableRow>
  );
};

const BugsChannelPage = () => {
  const navigate = useNavigate();
  const [message, setMessage] = useState("");
  const [conversation, setConversation] = useState(conversationData);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  
  useEffect(() => {
    document.title = "Bugs Channel | PageMind Admin";
    
    // Scroll to bottom of messages
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [conversation]);

  const handleSendMessage = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (message.trim()) {
      // Add user message
      const newUserMessage = {
        id: conversation.length + 1,
        sender: "You",
        avatarSrc: "/crew-agents/page-semantic.png",
        message,
        timestamp: "Just now",
        isUser: true
      };
      
      setConversation([...conversation, newUserMessage]);
      setMessage("");
      
      // Simulate response after a delay
      setTimeout(() => {
        const botResponse = {
          id: conversation.length + 2,
          sender: "BugHunter",
          avatarSrc: "/crew-agents/intent-checker.png",
          message: "I'll continue monitoring for any additional issues. The frontend team is making good progress on the permanent fix for the dashboard performance issue.",
          timestamp: "Just now",
          isUser: false
        };
        
        setConversation(prev => [...prev, botResponse]);
      }, 1000);
    }
  };

  return (
    <div className="container mx-auto p-6 h-[calc(100vh-4rem)] flex flex-col">
      {/* Header */}
      <div className="flex justify-between items-center mb-4 pb-4 border-b">
        <div className="flex items-center">
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={() => navigate('/insights/channels')}
            className="mr-2"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          
          <div className="flex items-center">
            <div className="flex items-center gap-1 bg-primary/10 rounded-full px-3 py-1.5">
              <Bug className="h-5 w-5 text-primary" />
              <div>
                <div className="font-bold flex items-center gap-2">
                  Bugs
                  <Badge variant="outline" className="text-xs bg-primary/5">Team Channel</Badge>
                  <Badge className="bg-red-500">1 Active</Badge>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div>
          <Button variant="outline" size="sm">
            Channel Settings
          </Button>
        </div>
      </div>
      
      {/* Bug Tracker */}
      <div className="mb-4 bg-background border rounded-md overflow-hidden">
        <div className="px-4 py-3 bg-muted/50 border-b flex justify-between items-center">
          <h3 className="font-medium flex items-center gap-2">
            <Bug className="h-4 w-4" />
            Active Bugs
          </h3>
          <Button size="sm" variant="outline" className="h-8">
            <Plus className="h-4 w-4 mr-1" />
            New Bug
          </Button>
        </div>
        <div className="px-4 py-2">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Issue</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Assignee</TableHead>
                <TableHead>Progress</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {activeBugs.map(bug => (
                <BugRow key={bug.id} bug={bug} />
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
      
      {/* Main content - split into chat and sidebar */}
      <div className="flex gap-4 flex-1 overflow-hidden">
        {/* Messages Container */}
        <div className="flex-1 overflow-y-auto px-4 py-4 bg-background/80 rounded-lg">
          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-blue-500 mt-0.5 flex-shrink-0" />
            <div>
              <h3 className="font-medium text-sm">Bugs Channel</h3>
              <p className="text-xs text-muted-foreground">This channel is for tracking, discussing, and resolving bugs. Critical issues will be highlighted.</p>
            </div>
          </div>
          
          {conversation.map(msg => (
            <ChatMessage key={msg.id} message={msg} />
          ))}
          <div ref={messagesEndRef} />
        </div>
        
        {/* Sidebar */}
        <div className="w-64 flex-shrink-0 hidden lg:block space-y-4">
          <div className="border rounded-md p-3 bg-background">
            <h3 className="font-medium mb-3 flex items-center">
              <AlertCircle className="h-4 w-4 mr-2 text-red-500" />
              Bug Summary
            </h3>
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">High:</span>
                <Badge className="bg-red-500">1</Badge>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">Medium:</span>
                <Badge className="bg-yellow-500">2</Badge>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">Low:</span>
                <Badge className="bg-blue-500">1</Badge>
              </div>
              <div className="flex justify-between items-center pt-2 border-t">
                <span className="text-sm font-medium">Total:</span>
                <Badge>4</Badge>
              </div>
            </div>
          </div>
          
          <div className="border rounded-md p-3 bg-background">
            <h3 className="font-medium mb-3">Team Members</h3>
            <div className="space-y-3">
              {teamMembers.map((member, index) => (
                <div key={index} className="flex items-center gap-2">
                  <div className="relative">
                    <Avatar>
                      <AvatarImage src={member.avatarSrc} alt={member.name} />
                      <AvatarFallback>{member.name[0]}</AvatarFallback>
                    </Avatar>
                    <div className={`absolute bottom-0 right-0 h-2.5 w-2.5 rounded-full border-2 border-background ${
                      member.status === 'online' ? 'bg-green-500' : 
                      member.status === 'away' ? 'bg-yellow-500' : 'bg-gray-400'
                    }`} />
                  </div>
                  <div>
                    <div className="text-sm font-medium">{member.name}</div>
                    <div className="text-xs text-muted-foreground">{member.role}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      
      {/* Message Input */}
      <div className="mt-4">
        <form onSubmit={handleSendMessage} className="flex gap-2">
          <Button
            type="button"
            variant="outline"
            size="icon"
            className="rounded-full"
          >
            <Paperclip className="h-4 w-4" />
          </Button>
          
          <Input
            value={message}
            onChange={e => setMessage(e.target.value)}
            placeholder="Message #bugs..."
            className="flex-1"
          />
          
          <Button
            type="submit"
            disabled={!message.trim()}
          >
            <Send className="h-4 w-4 mr-2" />
            Send
          </Button>
        </form>
      </div>
    </div>
  );
};

export default BugsChannelPage;